import { useContext, useEffect } from 'react';
import * as humps from 'humps';
import { AngularServicesContext } from 'react-app';
import uuid from 'react-uuid';

import { useAppDispatch } from 'redux/store';
import {
  getAiRolePlayConversation,
  getAiRolePlaySubmission,
  setAiRolePlaySubmissionCompleted,
  setAiRolePlaySubmissionPlaybackReady,
} from 'redux/actions/ai-role-play';
import { wrapThunkAction } from 'redux/utils';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';
import { SkillTag } from 'redux/schemas/models/skill-tag';
import PusherService from 'shared/services/pusher-service';
import { useTimelineService } from 'timelines/services/react-timeline-service';
import {
  AiRolePlaySubmissionPusherData,
  Message,
} from '../ai-conversation-modal/context';
import { ClosingReason } from '../ai-conversation-modal/stream-ai-response';

const AI_ROLE_PLAY_SUBMISSION_COMPLETED_EVENT = 'ai_role_play_submission_completed';
const AI_ROLE_PLAY_SUBMISSION_TERMINATED_EVENT = 'ai_role_play_submission_terminated';
const AI_ROLE_PLAY_FEEDBACK_READY_EVENT = 'ai_role_play_feedback_ready';
const AI_ROLE_PLAY_PLAYBACK_READY_EVENT = 'ai_role_play_playback_ready';

type PlaybackReadyPusherData = {
  aiRolePlayId: number;
  aiRolePlaySubmissionId: number;
  mergedPlaybackUrl: string;
  mergedPlaybackStatus: string;
};

interface UseAiRolePlayPusherEventsParams {
  anonymizedIdentifier: string;
  courseId: number | undefined;
  aiRolePlayId: number;
  courseCatalogId: string;
  pointsReceived: number;
  skillTags: SkillTag[];
  /** Set to false to skip subscribing entirely. */
  enabled?: boolean;
  /**
   * When true, skips opening the points/skill-tags modal.
   * Use when another hook instance (e.g. the lecture page component) is
   * responsible for showing the modal to avoid opening it twice.
   */
  skipModal?: boolean;
  interactionMode?: RolePlayInteractionMode;
  setMessages?: (messages: Message[]) => void;
}

/**
 * Custom hook to handle Pusher events for AI role play submissions.
 * Listens for submission completed and feedback ready events,
 * updates Redux state and shows points modal when applicable.
 */
const useAiRolePlaySubmissionPusherEvents = ({
  anonymizedIdentifier,
  courseId,
  aiRolePlayId,
  courseCatalogId,
  pointsReceived,
  skillTags,
  enabled = true,
  skipModal = false,
  interactionMode,
  setMessages,
}: UseAiRolePlayPusherEventsParams): void => {
  const dispatch = useAppDispatch();
  const { $uibModal } = useContext(AngularServicesContext) || {};

  const timelineService = useTimelineService();

  useEffect(() => {
    if (!enabled || !courseId) return () => {};

    const identifier = anonymizedIdentifier.substring(0, 10);
    const channel = PusherService.initializeUserCourseChannel(identifier, courseId);

    // Return empty cleanup function to satisfy ESLint consistent-return rule
    // (useEffect must return either undefined or a cleanup function consistently)
    if (!channel) return () => {};

    // Define named handlers so we can unbind the specific handler (not all listeners for the event)
    // This is important because PusherService caches channels per (identifier, course),
    // so multiple AI Role Play components on the same page share the same channel.
    const handleSubmissionCompleted = (pusherData: object) => {
      const data = humps.camelizeKeys(pusherData) as unknown as AiRolePlaySubmissionPusherData;
      // Only process event for this specific AI role play component
      if (data.aiRolePlayId !== aiRolePlayId) return;

      dispatch(setAiRolePlaySubmissionCompleted({
        aiRolePlayId: data.aiRolePlayId,
        aiRolePlaySubmissionId: data.aiRolePlaySubmissionId,
        pointsReceived: data.pointsReceived,
        finishedAt: data.finishedAt,
      }));

      // Fetch transcript for voice role play (fire regardless of closing reason)
      if (interactionMode === RolePlayInteractionMode.VOICE) {
        wrapThunkAction(dispatch(getAiRolePlayConversation({
          submissionId: data.aiRolePlaySubmissionId,
          catalogId: courseCatalogId,
        }))).then((res) => {
          const { chatMessages = [] } = (res?.payload as { chatMessages?: Message[] }) || {};
          setMessages(chatMessages.slice(1).map((m) => ({
            ...m,
            id: m.id ?? uuid(),
          })));
        }).catch(() => {});
      }
      // Skipping timeline update and modal display if submission was terminated, as no points are awarded and we don't want to show the points modal
      if (data.closingReason === ClosingReason.TERMINATED) {
        return;
      }

      timelineService.updateTimeline(data.lecturePageId);
      // Show modal if:
      // 1. There are skill tags (always show skill tags modal on completion), OR
      // 2. Points are being received for the first time (no previous points)
      const shouldShowSkillTagsModal = skillTags.length > 0;
      const shouldShowPointsModal = !pointsReceived && data.pointsReceived > 0;

      if (!skipModal && $uibModal && (shouldShowSkillTagsModal || shouldShowPointsModal)) {
        $uibModal.open({
          templateUrl: 'shared/templates/points-modal.html',
          windowClass: 'points-modal',
          controller: 'PointsModalCtrl as vm',
          resolve: {
            // Only pass points if this is the first time receiving points (not a repeat attempt)
            pointsReceived: shouldShowPointsModal ? data.pointsReceived : 0,
            leaderboardPoints: data.leaderboardPoints,
            leaderboardRank: data.leaderboardRank,
            priorLeaderboardRank: data.priorLeaderboardRank,
            extras: { skillTags },
          },
        });
      }
    };

    const handleFeedbackReady = (pusherData: object) => {
      const data = humps.camelizeKeys(pusherData) as unknown as AiRolePlaySubmissionPusherData;

      // Only process event for this specific AI role play component
      if (data.aiRolePlayId !== aiRolePlayId) return;

      wrapThunkAction(dispatch(getAiRolePlaySubmission({
        catalogId: courseCatalogId,
        submissionId: data.aiRolePlaySubmissionId,
      })));
    };

    const handlePlaybackReady = (pusherData: object) => {
      const data = humps.camelizeKeys(pusherData) as unknown as PlaybackReadyPusherData;
      if (data.aiRolePlayId !== aiRolePlayId) return;

      if (data.mergedPlaybackUrl) {
        dispatch(setAiRolePlaySubmissionPlaybackReady({
          aiRolePlaySubmissionId: data.aiRolePlaySubmissionId,
          mergedPlaybackUrl: data.mergedPlaybackUrl,
        }));
      }
    };

    channel.bind(AI_ROLE_PLAY_SUBMISSION_COMPLETED_EVENT, handleSubmissionCompleted);
    channel.bind(AI_ROLE_PLAY_SUBMISSION_TERMINATED_EVENT, handleSubmissionCompleted);
    channel.bind(AI_ROLE_PLAY_FEEDBACK_READY_EVENT, handleFeedbackReady);
    channel.bind(AI_ROLE_PLAY_PLAYBACK_READY_EVENT, handlePlaybackReady);

    return () => {
      // Pass the same function reference to unbind only this component's listener,
      // not all listeners for the event (which would happen with unbind(eventName) alone).
      // Note: The function is not called/executed here; it's used as a reference to identify
      // which specific listener to remove from Pusher's internal list.
      channel.unbind(AI_ROLE_PLAY_SUBMISSION_COMPLETED_EVENT, handleSubmissionCompleted);
      channel.unbind(AI_ROLE_PLAY_SUBMISSION_TERMINATED_EVENT, handleSubmissionCompleted);
      channel.unbind(AI_ROLE_PLAY_FEEDBACK_READY_EVENT, handleFeedbackReady);
      channel.unbind(AI_ROLE_PLAY_PLAYBACK_READY_EVENT, handlePlaybackReady);
    };
  }, [
    enabled,
    skipModal,
    anonymizedIdentifier,
    courseId,
    dispatch,
    courseCatalogId,
    $uibModal,
    aiRolePlayId,
    interactionMode,
    setMessages,
    pointsReceived,
    skillTags,
    timelineService,
  ]);
};

export default useAiRolePlaySubmissionPusherEvents;
